<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { CONVERSATION_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import { useTrack } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import MacroPreview from './MacroPreview.vue';

const props = defineProps({
  macro: {
    type: Object,
    required: true,
  },
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const COOLDOWN_MS = 10000; // 10 seconds cooldown window

const store = useStore();
const { t } = useI18n();

const isExecuting = ref(false);
const showPreview = ref(false);
const lastExecutedAt = ref(0);
const confirmReExecuteDialog = ref(null);

const doExecuteMacro = async () => {
  try {
    isExecuting.value = true;
    await store.dispatch('macros/execute', {
      macroId: props.macro.id,
      conversationIds: [props.conversationId],
    });
    useTrack(CONVERSATION_EVENTS.EXECUTED_A_MACRO);
    useAlert(t('MACROS.EXECUTE.EXECUTED_SUCCESSFULLY'));
    lastExecutedAt.value = Date.now();
  } catch (error) {
    useAlert(t('MACROS.ERROR'));
  } finally {
    isExecuting.value = false;
  }
};

const executeMacro = async () => {
  if (isExecuting.value) return;

  const timeSinceLastExecution = Date.now() - lastExecutedAt.value;

  // If executed recently (within cooldown window), show confirmation dialog
  if (lastExecutedAt.value > 0 && timeSinceLastExecution < COOLDOWN_MS) {
    confirmReExecuteDialog.value?.open();
    return;
  }

  await doExecuteMacro();
};

const onConfirmReExecute = async () => {
  confirmReExecuteDialog.value?.close();
  await doExecuteMacro();
};

const onCancelReExecute = () => {
  confirmReExecuteDialog.value?.close();
};

const toggleMacroPreview = () => {
  showPreview.value = !showPreview.value;
};

const closeMacroPreview = () => {
  showPreview.value = false;
};
</script>

<template>
  <div
    class="relative flex items-center justify-between leading-4 rounded-md h-10 pl-3 pr-2"
    :class="showPreview ? 'cursor-default' : 'drag-handle cursor-grab'"
  >
    <span
      class="overflow-hidden whitespace-nowrap text-ellipsis font-medium text-n-slate-12"
    >
      {{ macro.name }}
    </span>
    <div class="flex items-center gap-2 justify-end">
      <NextButton
        v-tooltip.left-start="$t('MACROS.EXECUTE.PREVIEW')"
        icon="i-lucide-info"
        slate
        faded
        sm
        @click="toggleMacroPreview"
      />
      <NextButton
        v-tooltip.left-start="$t('MACROS.EXECUTE.BUTTON_TOOLTIP')"
        icon="i-lucide-play"
        slate
        faded
        sm
        :is-loading="isExecuting"
        :disabled="isExecuting"
        @click="executeMacro"
      />
    </div>
    <transition name="menu-slide">
      <MacroPreview
        v-if="showPreview"
        v-on-clickaway="closeMacroPreview"
        :macro="macro"
      />
    </transition>

    <!-- Re-execution confirmation dialog -->
    <Dialog
      ref="confirmReExecuteDialog"
      type="edit"
      :title="$t('MACROS.EXECUTE.CONFIRM_RE_EXECUTE.TITLE')"
      :description="
        $t('MACROS.EXECUTE.CONFIRM_RE_EXECUTE.MESSAGE', {
          macroName: macro.name,
        })
      "
      :confirm-button-label="$t('MACROS.EXECUTE.CONFIRM_RE_EXECUTE.YES')"
      :cancel-button-label="$t('MACROS.EXECUTE.CONFIRM_RE_EXECUTE.NO')"
      width="sm"
      @confirm="onConfirmReExecute"
      @close="onCancelReExecute"
    />
  </div>
</template>
